import React, { useState, useEffect } from 'react';
import { validateEvent } from '../utils/validation.js';
import {
  VALID_PRECISIONS,
  VALID_DATING_BASIS,
  VALID_CATEGORIES,
  VALID_DEPTHS
} from '../constants.js';
import '../styles/EventEditor.css';

// Sub-editors
import ContentSectionEditor from './ContentSectionEditor';
import DatingEntryEditor from './DatingEntryEditor';
import LinkEditor from './LinkEditor';
import ScriptureRefEditor from './ScriptureRefEditor';

/**
 * EventEditor - Modal form component for creating and editing timeline events.
 *
 * Props:
 * - event: existing event object for edit mode, or null for create mode
 * - onSave(eventData): called on valid submission
 * - onCancel(): called on dismiss
 * - existingEvents: array of existing events (for link target selection)
 */
function EventEditor({ event, onSave, onCancel, existingEvents = [] }) {
  // Required fields
  const [title, setTitle] = useState('');
  const [startYear, setStartYear] = useState('');

  // Optional temporal fields
  const [summary, setSummary] = useState('');
  const [startMonth, setStartMonth] = useState('');
  const [startDay, setStartDay] = useState('');
  const [endYear, setEndYear] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [endDay, setEndDay] = useState('');
  const [precision, setPrecision] = useState('');
  const [datingBasis, setDatingBasis] = useState('');
  const [dateNote, setDateNote] = useState('');

  // Content and datings
  const [content, setContent] = useState([]);
  const [datings, setDatings] = useState([]);
  const [primaryDating, setPrimaryDating] = useState(0);

  // Organization
  const [depth, setDepth] = useState('');
  const [parentId, setParentId] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');
  const [color, setColor] = useState('');

  // References
  const [links, setLinks] = useState([]);
  const [externalLinks, setExternalLinks] = useState([]);
  const [scriptureRefs, setScriptureRefs] = useState([]);
  const [sources, setSources] = useState('');

  // Validation
  const [errors, setErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  // Pre-fill fields in edit mode
  useEffect(() => {
    if (event) {
      setTitle(event.title || '');
      setStartYear(event.startYear != null ? String(event.startYear) : '');
      setSummary(event.summary || '');
      setStartMonth(event.startMonth != null ? String(event.startMonth) : '');
      setStartDay(event.startDay != null ? String(event.startDay) : '');
      setEndYear(event.endYear != null ? String(event.endYear) : '');
      setEndMonth(event.endMonth != null ? String(event.endMonth) : '');
      setEndDay(event.endDay != null ? String(event.endDay) : '');
      setPrecision(event.precision || '');
      setDatingBasis(event.dating_basis || '');
      setDateNote(event.dateNote || '');
      setContent(event.content || []);
      setDatings(event.datings || []);
      setPrimaryDating(event.primaryDating || 0);
      setDepth(event.depth != null ? String(event.depth) : '');
      setParentId(event.parentId || '');
      setCategory(event.category || '');
      setTags(event.tags ? event.tags.join(', ') : '');
      setColor(event.color || '');
      setLinks(event.links || []);
      setExternalLinks(event.externalLinks || []);
      setScriptureRefs(event.scriptureRefs || []);
      setSources(event.sources ? event.sources.join('\n') : '');
    }
  }, [event]);

  /**
   * Build the event data object from form state.
   */
  function buildEventData() {
    const eventData = {
      title: title.trim(),
      startYear: startYear !== '' ? Number(startYear) : undefined,
    };

    // Optional temporal fields
    if (summary.trim()) eventData.summary = summary.trim();
    if (startMonth !== '') eventData.startMonth = Number(startMonth);
    if (startDay !== '') eventData.startDay = Number(startDay);
    if (endYear !== '') eventData.endYear = Number(endYear);
    if (endMonth !== '') eventData.endMonth = Number(endMonth);
    if (endDay !== '') eventData.endDay = Number(endDay);
    if (precision) eventData.precision = precision;
    if (datingBasis) eventData.dating_basis = datingBasis;
    if (dateNote.trim()) eventData.dateNote = dateNote.trim();

    // Content and datings
    if (content.length > 0) eventData.content = content;
    if (datings.length > 0) {
      eventData.datings = datings;
      eventData.primaryDating = primaryDating;
    }

    // Organization
    if (depth !== '') eventData.depth = Number(depth);
    if (parentId.trim()) eventData.parentId = parentId.trim();
    if (category) eventData.category = category;
    if (tags.trim()) {
      eventData.tags = tags.split(',').map(t => t.trim()).filter(t => t.length > 0);
    }
    if (color.trim()) eventData.color = color.trim();

    // References
    if (links.length > 0) eventData.links = links;
    if (externalLinks.length > 0) eventData.externalLinks = externalLinks;
    if (scriptureRefs.length > 0) eventData.scriptureRefs = scriptureRefs;
    if (sources.trim()) {
      eventData.sources = sources.split('\n').map(s => s.trim()).filter(s => s.length > 0);
    }

    return eventData;
  }

  /**
   * Map validation errors to field-specific errors for inline display.
   */
  function mapFieldErrors(validationErrors) {
    const mapped = {};
    for (const err of validationErrors) {
      if (err.toLowerCase().includes('title')) {
        mapped.title = err;
      } else if (err.toLowerCase().includes('start year')) {
        mapped.startYear = err;
      } else if (err.toLowerCase().includes('end year')) {
        mapped.endYear = err;
      } else if (err.toLowerCase().includes('precision')) {
        mapped.precision = err;
      } else if (err.toLowerCase().includes('dating_basis')) {
        mapped.datingBasis = err;
      } else if (err.toLowerCase().includes('category')) {
        mapped.category = err;
      } else if (err.toLowerCase().includes('depth')) {
        mapped.depth = err;
      }
    }
    return mapped;
  }

  function handleSubmit(e) {
    e.preventDefault();
    setErrors([]);
    setFieldErrors({});

    const eventData = buildEventData();
    const result = validateEvent(eventData);

    if (!result.valid) {
      setErrors(result.errors);
      setFieldErrors(mapFieldErrors(result.errors));
      return;
    }

    onSave(eventData);
  }

  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  }

  return (
    <div className="event-editor-overlay" onClick={handleOverlayClick}>
      <div className="event-editor-modal" role="dialog" aria-label={event ? 'Edit Event' : 'Create Event'}>
        <div className="event-editor-header">
          <h2>{event ? 'Edit Event' : 'Create Event'}</h2>
          <button
            type="button"
            className="event-editor-close-btn"
            onClick={onCancel}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form className="event-editor-form" onSubmit={handleSubmit} noValidate>
          {/* General validation errors */}
          {errors.length > 0 && (
            <div className="event-editor-errors" role="alert">
              <ul>
                {errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Required Fields Section */}
          <fieldset className="event-editor-section">
            <legend>Required Fields</legend>

            <div className={`event-editor-field ${fieldErrors.title ? 'has-error' : ''}`}>
              <label htmlFor="event-title">Title *</label>
              <input
                id="event-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Event title"
                required
              />
              {fieldErrors.title && (
                <span className="event-editor-field-error">{fieldErrors.title}</span>
              )}
            </div>

            <div className={`event-editor-field ${fieldErrors.startYear ? 'has-error' : ''}`}>
              <label htmlFor="event-start-year">Start Year *</label>
              <input
                id="event-start-year"
                type="number"
                value={startYear}
                onChange={(e) => setStartYear(e.target.value)}
                placeholder="e.g., -586 for 586 BC, 70 for AD 70"
              />
              <span className="event-editor-field-hint">Negative for BC, positive for AD. Year 0 does not exist.</span>
              {fieldErrors.startYear && (
                <span className="event-editor-field-error">{fieldErrors.startYear}</span>
              )}
            </div>
          </fieldset>

          {/* Date Details Section */}
          <fieldset className="event-editor-section">
            <legend>Date Details</legend>

            <div className="event-editor-row">
              <div className="event-editor-field">
                <label htmlFor="event-start-month">Start Month</label>
                <input
                  id="event-start-month"
                  type="number"
                  min="1"
                  max="12"
                  value={startMonth}
                  onChange={(e) => setStartMonth(e.target.value)}
                  placeholder="1-12"
                />
              </div>

              <div className="event-editor-field">
                <label htmlFor="event-start-day">Start Day</label>
                <input
                  id="event-start-day"
                  type="number"
                  min="1"
                  max="31"
                  value={startDay}
                  onChange={(e) => setStartDay(e.target.value)}
                  placeholder="1-31"
                />
              </div>
            </div>

            <div className={`event-editor-field ${fieldErrors.endYear ? 'has-error' : ''}`}>
              <label htmlFor="event-end-year">End Year</label>
              <input
                id="event-end-year"
                type="number"
                value={endYear}
                onChange={(e) => setEndYear(e.target.value)}
                placeholder="e.g., -516 for 516 BC"
              />
              {fieldErrors.endYear && (
                <span className="event-editor-field-error">{fieldErrors.endYear}</span>
              )}
            </div>

            <div className="event-editor-row">
              <div className="event-editor-field">
                <label htmlFor="event-end-month">End Month</label>
                <input
                  id="event-end-month"
                  type="number"
                  min="1"
                  max="12"
                  value={endMonth}
                  onChange={(e) => setEndMonth(e.target.value)}
                  placeholder="1-12"
                />
              </div>

              <div className="event-editor-field">
                <label htmlFor="event-end-day">End Day</label>
                <input
                  id="event-end-day"
                  type="number"
                  min="1"
                  max="31"
                  value={endDay}
                  onChange={(e) => setEndDay(e.target.value)}
                  placeholder="1-31"
                />
              </div>
            </div>

            <div className={`event-editor-field ${fieldErrors.precision ? 'has-error' : ''}`}>
              <label htmlFor="event-precision">Precision</label>
              <select
                id="event-precision"
                value={precision}
                onChange={(e) => setPrecision(e.target.value)}
              >
                <option value="">-- Select --</option>
                {VALID_PRECISIONS.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              {fieldErrors.precision && (
                <span className="event-editor-field-error">{fieldErrors.precision}</span>
              )}
            </div>

            <div className={`event-editor-field ${fieldErrors.datingBasis ? 'has-error' : ''}`}>
              <label htmlFor="event-dating-basis">Dating Basis</label>
              <select
                id="event-dating-basis"
                value={datingBasis}
                onChange={(e) => setDatingBasis(e.target.value)}
              >
                <option value="">-- Select --</option>
                {VALID_DATING_BASIS.map(d => (
                  <option key={d} value={d}>{d.replace(/_/g, ' ')}</option>
                ))}
              </select>
              {fieldErrors.datingBasis && (
                <span className="event-editor-field-error">{fieldErrors.datingBasis}</span>
              )}
            </div>

            <div className="event-editor-field">
              <label htmlFor="event-date-note">Date Note</label>
              <input
                id="event-date-note"
                type="text"
                value={dateNote}
                onChange={(e) => setDateNote(e.target.value)}
                placeholder="Notes about the dating"
              />
            </div>
          </fieldset>

          {/* Summary Section */}
          <fieldset className="event-editor-section">
            <legend>Summary</legend>

            <div className="event-editor-field">
              <label htmlFor="event-summary">Summary</label>
              <textarea
                id="event-summary"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="Brief summary of the event"
                rows={3}
              />
            </div>
          </fieldset>

          {/* Content Sections */}
          <fieldset className="event-editor-section">
            <legend>Content Sections</legend>
            <ContentSectionEditor content={content} onChange={setContent} />
          </fieldset>

          {/* Datings Section */}
          <fieldset className="event-editor-section">
            <legend>Alternative Datings</legend>
            <DatingEntryEditor
              datings={datings}
              primaryDating={primaryDating}
              onChange={setDatings}
              onPrimaryChange={setPrimaryDating}
            />
          </fieldset>

          {/* Organization Section */}
          <fieldset className="event-editor-section">
            <legend>Organization</legend>

            <div className={`event-editor-field ${fieldErrors.depth ? 'has-error' : ''}`}>
              <label htmlFor="event-depth">Depth Level</label>
              <select
                id="event-depth"
                value={depth}
                onChange={(e) => setDepth(e.target.value)}
              >
                <option value="">-- Select --</option>
                {VALID_DEPTHS.map(d => (
                  <option key={d} value={d}>
                    {d} - {['Era', 'Period', 'Event', 'Sub-event'][d]}
                  </option>
                ))}
              </select>
              {fieldErrors.depth && (
                <span className="event-editor-field-error">{fieldErrors.depth}</span>
              )}
            </div>

            <div className="event-editor-field">
              <label htmlFor="event-parent-id">Parent Event ID</label>
              <input
                id="event-parent-id"
                type="text"
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                placeholder="ID of parent event (optional)"
              />
            </div>

            <div className={`event-editor-field ${fieldErrors.category ? 'has-error' : ''}`}>
              <label htmlFor="event-category">Category</label>
              <select
                id="event-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">-- Select --</option>
                {VALID_CATEGORIES.map(c => (
                  <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
                ))}
              </select>
              {fieldErrors.category && (
                <span className="event-editor-field-error">{fieldErrors.category}</span>
              )}
            </div>

            <div className="event-editor-field">
              <label htmlFor="event-tags">Tags</label>
              <input
                id="event-tags"
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="Comma-separated tags"
              />
            </div>

            <div className="event-editor-field">
              <label htmlFor="event-color">Color</label>
              <div className="event-editor-color-field">
                <input
                  id="event-color-picker"
                  type="color"
                  value={color || '#4A90D9'}
                  onChange={(e) => setColor(e.target.value)}
                  className="event-editor-color-picker"
                />
                <input
                  id="event-color"
                  type="text"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder="#hex color (optional)"
                />
                {color && (
                  <button
                    type="button"
                    className="event-editor-color-clear"
                    onClick={() => setColor('')}
                    aria-label="Clear color"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </fieldset>

          {/* Links Section */}
          <fieldset className="event-editor-section">
            <legend>Event Links</legend>
            <LinkEditor links={links} onChange={setLinks} existingEvents={existingEvents} />
          </fieldset>

          {/* External Links */}
          <fieldset className="event-editor-section">
            <legend>External Links</legend>
            <div className="event-editor-sub-editor-placeholder">
              <p className="event-editor-placeholder-text">
                {externalLinks.length > 0
                  ? `${externalLinks.length} external link(s) configured`
                  : 'External link fields will be available here'}
              </p>
            </div>
          </fieldset>

          {/* Scripture References */}
          <fieldset className="event-editor-section">
            <legend>Scripture References</legend>
            <ScriptureRefEditor scriptureRefs={scriptureRefs} onChange={setScriptureRefs} />
          </fieldset>

          {/* Sources */}
          <fieldset className="event-editor-section">
            <legend>Sources</legend>

            <div className="event-editor-field">
              <label htmlFor="event-sources">Source Citations</label>
              <textarea
                id="event-sources"
                value={sources}
                onChange={(e) => setSources(e.target.value)}
                placeholder="One source per line"
                rows={3}
              />
            </div>
          </fieldset>

          {/* Form Actions */}
          <div className="event-editor-actions">
            <button type="button" className="event-editor-cancel-btn" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="event-editor-save-btn">
              {event ? 'Save Changes' : 'Create Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EventEditor;
